/**
 * IndexedDB utility for storing File objects.
 * Used for persisting photo and document uploads across sessions.
 */

const DB_NAME = 'SecondVieDB';
const DB_VERSION = 1;
const FILES_STORE = 'files';

interface StoredFile {
  key: string;
  file: File;
  timestamp: number;
}

/**
 * Opens or creates the IndexedDB database.
 */
function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = event => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(FILES_STORE)) {
        db.createObjectStore(FILES_STORE, { keyPath: 'key' });
      }
    };
  });
}

/**
 * Stores an array of File objects in IndexedDB.
 * @param key - Unique identifier for this file collection (e.g., 'draft_photos', 'draft_documents')
 * @param files - Array of File objects to store
 */
export async function saveFiles(key: string, files: File[]): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction([FILES_STORE], 'readwrite');
    const store = transaction.objectStore(FILES_STORE);

    const storedFile: StoredFile = {
      key,
      file: files as unknown as File, // Store array as single entry
      timestamp: Date.now(),
    };

    // Store files array
    const request = store.put(storedFile);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to save files for key: ${key}`));

      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('Error saving files to IndexedDB:', error);
    throw error;
  }
}

/**
 * Retrieves an array of File objects from IndexedDB.
 * @param key - Unique identifier for the file collection
 * @returns Array of File objects, or empty array if not found
 */
export async function getFiles(key: string): Promise<File[]> {
  try {
    const db = await openDB();
    const transaction = db.transaction([FILES_STORE], 'readonly');
    const store = transaction.objectStore(FILES_STORE);
    const request = store.get(key);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const result = request.result as StoredFile | undefined;

        if (result && result.file) {
          // Handle both single File and File array
          const files = Array.isArray(result.file) ? result.file : [result.file];
          resolve(files);
        } else {
          resolve([]);
        }
      };

      request.onerror = () => {
        reject(new Error(`Failed to retrieve files for key: ${key}`));
      };

      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('Error retrieving files from IndexedDB:', error);
    return [];
  }
}

/**
 * Removes file collection from IndexedDB.
 * @param key - Unique identifier for the file collection to remove
 */
export async function removeFiles(key: string): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction([FILES_STORE], 'readwrite');
    const store = transaction.objectStore(FILES_STORE);
    const request = store.delete(key);

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error(`Failed to remove files for key: ${key}`));

      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('Error removing files from IndexedDB:', error);
    throw error;
  }
}

/**
 * Clears all stored files from IndexedDB.
 * Useful for cleanup or testing.
 */
export async function clearAllFiles(): Promise<void> {
  try {
    const db = await openDB();
    const transaction = db.transaction([FILES_STORE], 'readwrite');
    const store = transaction.objectStore(FILES_STORE);
    const request = store.clear();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error('Failed to clear all files'));

      transaction.oncomplete = () => db.close();
    });
  } catch (error) {
    console.error('Error clearing all files from IndexedDB:', error);
    throw error;
  }
}
